import dayjs from "dayjs";
import React from "react";
import { useAppStore } from "../../stores";
import "./index.scss";

interface CardProps {
	_id: string;
	contentTypeLink: string;
	displayName: string;
	description: string;
	contentTypeImage?: string;
	contentTypeImageAltText?: string;
	modifiedTime?: string;
	publish?: { from?: string } | undefined;
	viewed?: number;
	content?: string;
	linkContent?: {
		linkTitle: string;
		linkUrl: string;
	}[];
	projectStatus?: string;
}

export const BlueCardTheme = ({ _id, contentTypeLink, displayName, description, projectStatus }: CardProps) => {
	const statusColors: Record<string, string> = {
		approved: "#4CAF50",
		rejected: "#F44336",
		in_progress: "#FFC107",
	};
	const appStore = useAppStore((state) => state);
	return (
		<a key={`article-${_id}`} href={contentTypeLink} className="card cards-groups">
			<div className="card-groups-body">
				<div className="head">
					<h5 className="card-title">{displayName}</h5>
					{projectStatus && (
						<p
							className="projectBadge"
							style={{
								backgroundColor: statusColors[projectStatus] || "#9E9E9E",
							}}
						>
							{appStore.phrases?.[projectStatus] ?? projectStatus}
						</p>
					)}
				</div>

				<p className="card-text">{description}</p>
			</div>
		</a>
	);
};

export const whiteCardTheme = ({
	_id,
	contentTypeLink,
	contentTypeImage,
	contentTypeImageAltText,
	displayName,
	description,
	linkContent,
}: CardProps) => {
	const links = Array.isArray(linkContent) ? linkContent : linkContent ? [linkContent] : [];

	return (
		<div key={`article-${_id}`} className="white-card-theme">
			<div className="white-card-body">
				<div>
					<div
						className="white-card-image"
						style={{ backgroundImage: `url(${contentTypeImage})` }}
						title={contentTypeImageAltText}
					></div>
				</div>
				<div className="white-card-content">
					<a className="text-start p-0" href={contentTypeLink}>
						<h5 className="card-title">{displayName}</h5>
					</a>
					<p className="card-text">{description}</p>
					{links.map((link, index) => (
						<a
							key={`article-link-${_id}-${index}`}
							href={link.linkUrl}
							className="white-card-link"
							target="_blank"
							rel="noopener noreferrer"
						>
							<i className="fa-solid fa-up-right-from-square"></i> {link.linkTitle}
						</a>
					))}
				</div>
			</div>
		</div>
	);
};

export const defaultCardTheme = ({
	_id,
	contentTypeLink,
	displayName,
	description,
	contentTypeImage,
	contentTypeImageAltText,
	modifiedTime,
	publish,
	viewed,
}: CardProps) => {
	return (
		<a key={`article-${_id}`} href={contentTypeLink} className="card shadow-sm mb-2">
			<div className="row g-0">
				<div className="col-md-3 rounded overflow-hidden bg-light">
					{contentTypeImage && (
						<div className="col-md-3 rounded overflow-hidden bg-light w-100 h-100">
							<div
								className="main-image"
								style={{ backgroundImage: `url(${contentTypeImage})` }}
								title={contentTypeImageAltText}
							></div>
						</div>
					)}
				</div>

				<div className="col-md-9">
					<div className="card-body">
						<h5 className="card-title">{displayName}</h5>

						<p className="d-flex gap-3 text-muted">
							<span className="d-flex gap-2">
								<i className="fa-solid fa-calendar"></i>
								<small>{dayjs(publish?.from ?? modifiedTime).format("DD.MM.YYYY")}</small>
							</span>

							<span className="d-flex gap-2">
								<i className="fa-solid fa-eye"></i>
								<small>{`${viewed ?? 0}`}</small>
							</span>
						</p>

						<p className="card-text">{description}</p>
					</div>
				</div>
			</div>
		</a>
	);
};
